import type { SVGAttributes } from "react"

import { cx } from "@uui/utils/cx"

/*
 * A school contact's role, drawn as a glyph. The six roles
 * are the product's ContactRole enum. Each glyph sits on the mark's 16 grid at
 * the wordmark's stroke (1.7, round caps and joins), the ink left open, with
 * one articulation in do.500 standing where the closing edge would be: the
 * door, the heart, the tassel, the handle, the sleep mark. "other" is a person
 * with nothing carried and no articulation.
 */
export type ContactRole =
  | "principal"
  | "wellness_coordinator"
  | "supervising_teacher"
  | "administrative"
  | "after_hours"
  | "other"

/** [ink paths, articulation paths] */
const GLYPH: Record<ContactRole, [string[], string[]]> = {
  principal: [
    ["M2.6 13.4V7.4L8 2.6l5.4 4.8v6"],
    ["M6.2 13.4v-2.8a1.8 1.8 0 0 1 3.6 0v2.8"],
  ],
  wellness_coordinator: [
    ["M2.6 8.6c0 3.8 2.4 5.6 5.4 5.6s5.4-1.8 5.4-5.6"],
    [
      "M8 9.6C7.2 8.6 4.6 7.4 4.6 5.2c0-1.6 1.2-2.6 2.4-2.6.5 0 .8.2 1 .5.2-.3.5-.5 1-.5 1.2 0 2.4 1 2.4 2.6 0 2.2-2.6 3.4-3.4 4.4Z",
    ],
  ],
  supervising_teacher: [
    ["M1.6 7 8 3.6 14.4 7 8 10.4Z", "M4 8.3v2.6c0 1.5 2 2 4 2"],
    ["M14.4 7.2c0 2 .1 3.4-.9 5"],
  ],
  administrative: [
    ["M2.6 6.4v6.8h10.8V6.4"],
    ["M5.6 6.4V4.8a2.4 2.4 0 0 1 4.8 0v1.6"],
  ],
  after_hours: [
    ["M8.85 4.96a4.9 4.9 0 1 0 0 8.48 4.36 4.36 0 0 1 0-8.48Z"],
    ["M12 2.4h2.8L12 5.8h2.8"],
  ],
  other: [
    [
      "M8 2.7a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5Z",
      "M2.6 14c0-3.4 2.4-4.8 5.4-4.8s5.4 1.4 5.4 4.8",
    ],
    [],
  ],
}

export const ROLE_LABEL: Record<ContactRole, string> = {
  principal: "principal",
  wellness_coordinator: "wellbeing coordinator",
  supervising_teacher: "supervising teacher",
  administrative: "administrative",
  after_hours: "after hours",
  other: "other",
}

export type RoleGlyphProps = Omit<SVGAttributes<SVGSVGElement>, "role"> & {
  role: ContactRole
}

/** Strokes in currentColor; size it with a Tailwind size class. */
export function RoleGlyph({ role, className, ...rest }: RoleGlyphProps) {
  const [ink, articulation] = GLYPH[role]
  return (
    <svg
      viewBox="0 0 16 16"
      aria-hidden="true"
      focusable="false"
      className={cx("block shrink-0 overflow-visible", className)}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      {...rest}
    >
      {ink.map((d) => (
        <path key={d} d={d} />
      ))}
      {articulation.map((d) => (
        <path key={d} d={d} className="stroke-do-500" />
      ))}
    </svg>
  )
}
